import { RoutesPaths } from "../../constants/user-routes"
import { BA_STEPS } from "../../Containers/GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../Containers/GettingStartedPage/utils/onboardingSteps"
import { FEATURES_STEP_PATH, onboardingStepPath } from "../utils"
import { isOnboardingFinished } from "./isOnboardingFinished"

// The two Chargeback products a merchant picks on "Choose your features". `alerts` is Prevent:
// the key predates the product name and is what the rest of the app already checks.
export const FEATURES = {
  PREVENT: "alerts",
  RECOVER: "recover"
}

export const ALL_FEATURES = [FEATURES.PREVENT, FEATURES.RECOVER]

export const CHOICE_FLOWS = {
  ONBOARDING: "onboarding",
  BUSINESS: "business"
}

// The choice lives in the browser only (OB-X-05). The primary onboarding and the add-a-business
// flow keep separate answers: a merchant who set their first business up for Prevent may well
// add a Recover-only business later, and the second choice must not overwrite the first.
const STORAGE_KEYS = {
  [CHOICE_FLOWS.ONBOARDING]: "onboarding:selectedFeatures",
  [CHOICE_FLOWS.BUSINESS]: "onboarding:selectedFeatures:business"
}

// Browser storage outlives the session, so an answer is stored together with the user it belongs
// to and only read back for that user. Without this, the next account signed in on the same
// machine inherits the previous one's choice and sees the features step already done. The owner
// is mirrored from the store (see `src/store.js`), the same way the Incognito flag is.
let currentOwner = null

export const syncProductChoiceOwner = (state) => {
  currentOwner = state?.auth?.userInfo?.id ?? null
}

const normalize = (value) =>
  Array.isArray(value) ? ALL_FEATURES.filter((key) => value.includes(key)) : []

// What is stored for the flow: a non-empty array, or null when nothing was ever chosen there.
// Empty and missing read the same on purpose — an empty choice cannot be saved (Continue is
// disabled at zero cards), so an empty array can only be a leftover and means "not chosen".
// An entry saved by another user, or one from before owners were recorded, reads as "not chosen"
// too: it is somebody else's answer, or nobody's.
export const readProductChoice = (flow = CHOICE_FLOWS.ONBOARDING) => {
  try {
    const entry = JSON.parse(localStorage.getItem(STORAGE_KEYS[flow]))

    if (!entry || Array.isArray(entry) || entry.owner !== currentOwner) return null

    const stored = normalize(entry.features)

    return stored.length ? stored : null
  } catch {
    return null
  }
}

export const saveProductChoice = (features, flow = CHOICE_FLOWS.ONBOARDING) => {
  try {
    localStorage.setItem(
      STORAGE_KEYS[flow],
      JSON.stringify({ owner: currentOwner, features: normalize(features) })
    )
  } catch {
    // Storage can be unavailable (private mode, quota); the page still works for this visit.
  }
}

export const clearProductChoice = (flow = CHOICE_FLOWS.ONBOARDING) => {
  try {
    localStorage.removeItem(STORAGE_KEYS[flow])
  } catch {
    // Nothing to clear.
  }
}

// "Progress" in the FRD's sense: at least one step beyond email verification is complete
// (OB-X-07). A verified email alone does not count, otherwise every brand-new user would look
// like a pre-release account (OB-X-08).
export const hasOnboardingProgress = ({ flow, userInfo, nextCreateAccountStep }) => {
  if (flow === CHOICE_FLOWS.BUSINESS) {
    return (nextCreateAccountStep ?? 0) > BA_STEPS.COMPANY
  }

  return isOnboardingFinished(userInfo) || (userInfo?.next_step ?? 0) > OB_STEPS.COMPANY
}

// The choice every rule downstream reads. Three answers:
//  - a stored choice wins;
//  - progress but nothing stored → Prevent only. The account started before Recover existed, so
//    its user never saw the screen and cannot have picked Recover (OB-X-07); the features step
//    then reads as finished with Prevent recorded;
//  - no progress and nothing stored → null: "not answered yet". Callers must treat null as the
//    full sequence with nothing skipped (OB-X-08), never as an empty choice.
export const resolveProductChoice = ({ flow = CHOICE_FLOWS.ONBOARDING, hasProgress }) => {
  const stored = readProductChoice(flow)

  if (stored) return stored

  return hasProgress ? [FEATURES.PREVENT] : null
}

export const isFeaturesStepDone = ({ flow, hasProgress }) =>
  resolveProductChoice({ flow, hasProgress }) !== null

export const onboardingResumePath = (userInfo) => {
  const flow = CHOICE_FLOWS.ONBOARDING
  const nextStep = userInfo?.next_step
  const featuresDone = isFeaturesStepDone({
    flow,
    hasProgress: hasOnboardingProgress({ flow, userInfo })
  })

  if (nextStep === OB_STEPS.COMPANY && !featuresDone) return FEATURES_STEP_PATH.onboarding

  return onboardingStepPath[nextStep]
}

// Whether the resolved choice includes a product. Null (not answered) includes everything, so
// the flow before the choice shows every step (OB-X-08).
export const choiceIncludes = (choice, feature) => choice === null || choice.includes(feature)

export const sameChoice = (a, b) => {
  const left = normalize(a)
  const right = normalize(b)

  return left.length === right.length && left.every((key) => right.includes(key))
}

// Processors Recover can read disputes from. The list endpoint says so per processor
// (`available_for`); this set is the fallback for a payload that does not carry it yet.
export const RECOVER_PROCESSOR_SERVICES = ["stripe", "shopify"]

export const supportsRecover = (processor) =>
  Array.isArray(processor?.available_for)
    ? processor.available_for.includes(FEATURES.RECOVER)
    : RECOVER_PROCESSOR_SERVICES.includes(processor?.service_name)

// Where the flow lands once it is finished (OB-10-05..07): a merchant who chose Recover goes
// into its setup wizard, anyone else to Home. Decided from the recorded choice alone.
export const RECOVER_CONFIGURE_PATH = RoutesPaths.recover.configure_section("integrations")
export const HOME_PATH = RoutesPaths.home

export const landingPathAfterOnboarding = (choice) =>
  choice !== null && choice.includes(FEATURES.RECOVER) ? RECOVER_CONFIGURE_PATH : HOME_PATH
